import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import SaveIcon from '@mui/icons-material/Save';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Spinner from '../../../components/Spinner';
import Toast from '../../../components/Toast';
import TenantService from '../../../services/tenant.service';

export interface ConfigKeyItem {
  key_id: number;
  key_code: string;
  display_label: string;
  help_text: string | null;
  data_type: 'text' | 'number' | 'boolean' | 'date' | 'dropdown';
  validation_rule: string | null;
  default_value: string | null;
  is_mandatory: boolean;
  group_category: string | null;
  display_order: number;
  current_value: string | null;
  last_updated_by: number | null;
  last_updated_by_name: string | null;
  last_updated_at: string | null;
}

interface ConfigTabProps {
  tenantId: string | number;
  /** US-029 ST-004: bubbles up after every successful save so the Activate button can re-check without a page reload. */
  onValueSaved?: () => void;
}

function parseDropdownOptions(validationRule: string | null): string[] {
  try {
    const parsed = validationRule ? JSON.parse(validationRule) : [];
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/** Mirrors the backend's per-data-type check (tenant-config-value.service.ts) for immediate inline feedback. */
function isValueValid(item: ConfigKeyItem, value: string): boolean {
  if (value === '') return false;
  switch (item.data_type) {
    case 'dropdown':
      return parseDropdownOptions(item.validation_rule).includes(value);
    case 'number':
      return Number.isFinite(Number(value));
    case 'boolean':
      return value === 'true' || value === 'false';
    case 'date':
      return !Number.isNaN(Date.parse(value));
    default:
      return true;
  }
}

const ConfigTab: React.FC<ConfigTabProps> = ({ tenantId, onValueSaved }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<ConfigKeyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [errors, setErrors] = useState<Record<number, boolean>>({});
  const [saving, setSaving] = useState<Record<number, boolean>>({});

  const load = useCallback(() => {
    setLoading(true);
    TenantService.getConfig(tenantId)
      .then((response: any) => {
        const list: ConfigKeyItem[] = response?.data?.items ?? [];
        setItems(list);
        const initialDrafts: Record<number, string> = {};
        list.forEach((item) => {
          initialDrafts[item.key_id] = item.current_value ?? item.default_value ?? '';
        });
        setDrafts(initialDrafts);
      })
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => {
    const byGroup = new Map<string, ConfigKeyItem[]>();
    items.forEach((item) => {
      const key = item.group_category ?? String(t('tenants.detail.pageTexts.configuration.generalGroup'));
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key)!.push(item);
    });
    return Array.from(byGroup.entries());
  }, [items, t]);

  const handleChange = useCallback((keyId: number, value: string) => {
    setDrafts((prev) => ({ ...prev, [keyId]: value }));
  }, []);

  const handleBlur = useCallback(
    (item: ConfigKeyItem) => {
      const value = drafts[item.key_id] ?? '';
      setErrors((prev) => ({ ...prev, [item.key_id]: !isValueValid(item, value) }));
    },
    [drafts],
  );

  const handleSave = useCallback(
    async (item: ConfigKeyItem) => {
      const value = drafts[item.key_id] ?? '';
      if (!isValueValid(item, value)) {
        setErrors((prev) => ({ ...prev, [item.key_id]: true }));
        return;
      }
      setSaving((prev) => ({ ...prev, [item.key_id]: true }));
      try {
        await TenantService.setConfigValue(tenantId, item.key_id, value);
        toast.success(<Toast message={t('tenants.detail.pageTexts.configuration.saveSuccess')} />, { icon: false });
        setItems((prev) =>
          prev.map((row) =>
            row.key_id === item.key_id ? { ...row, current_value: value } : row,
          ),
        );
        onValueSaved?.();
      } finally {
        setSaving((prev) => ({ ...prev, [item.key_id]: false }));
      }
    },
    [drafts, tenantId, t, onValueSaved],
  );

  const renderInput = (item: ConfigKeyItem) => {
    const value = drafts[item.key_id] ?? '';
    const hasError = !!errors[item.key_id];

    if (item.data_type === 'boolean') {
      return (
        <Switch
          checked={value === 'true'}
          onChange={(e) => handleChange(item.key_id, e.target.checked ? 'true' : 'false')}
          onBlur={() => handleBlur(item)}
        />
      );
    }

    if (item.data_type === 'dropdown') {
      const options = parseDropdownOptions(item.validation_rule);
      return (
        <TextField
          select
          size="small"
          fullWidth
          value={value}
          error={hasError}
          onChange={(e) => handleChange(item.key_id, e.target.value)}
          onBlur={() => handleBlur(item)}
        >
          {options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    const inputType = item.data_type === 'number' ? 'number' : item.data_type === 'date' ? 'date' : 'text';
    return (
      <TextField
        size="small"
        fullWidth
        type={inputType}
        value={value}
        error={hasError}
        InputLabelProps={inputType === 'date' ? { shrink: true } : undefined}
        onChange={(e) => handleChange(item.key_id, e.target.value)}
        onBlur={() => handleBlur(item)}
      />
    );
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <Box>
      {groups.map(([groupName, groupItems]) => (
        <Accordion key={groupName} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>{groupName}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {groupItems.map((item) => {
              const isUnsetMandatory = item.is_mandatory && !item.current_value && !item.default_value;
              return (
                <Box
                  key={item.key_id}
                  display="flex"
                  alignItems="flex-start"
                  gap={2}
                  py={1.5}
                  px={isUnsetMandatory ? 1.5 : 0}
                  sx={isUnsetMandatory ? { backgroundColor: 'warning.light', borderRadius: 2 } : undefined}
                >
                  <Box flex="0 0 260px" display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight={500}>
                      {item.display_label}
                    </Typography>
                    {item.help_text && (
                      <Tooltip title={item.help_text}>
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </Tooltip>
                    )}
                    {isUnsetMandatory && (
                      <Chip
                        size="small"
                        color="warning"
                        label={t('tenants.detail.pageTexts.configuration.required')}
                      />
                    )}
                  </Box>
                  <Box flex="1 1 auto">
                    {renderInput(item)}
                    {errors[item.key_id] && (
                      <FormHelperText error>
                        {t('tenants.detail.pageTexts.configuration.invalidValue')}
                      </FormHelperText>
                    )}
                    {item.last_updated_by_name && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('tenants.detail.pageTexts.configuration.lastUpdatedBy', { name: item.last_updated_by_name })}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    aria-label="save"
                    color="primary"
                    disabled={!!errors[item.key_id] || !!saving[item.key_id]}
                    onClick={() => handleSave(item)}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </AccordionDetails>
        </Accordion>
      ))}
      {groups.length === 0 && (
        <Typography color="text.secondary">{t('tenants.detail.pageTexts.configuration.empty')}</Typography>
      )}
    </Box>
  );
};

export default ConfigTab;
